import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { ADMIN_NAV } from "@/lib/admin-nav";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { checkSitemap, observabilityEnv } from "@/lib/observability.functions";
import { ExternalLink, CheckCircle2, AlertCircle } from "lucide-react";

export const Route = createFileRoute("/admin/observability")({
  head: () => ({
    meta: [
      { title: "Observability — Command Center" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <ProtectedRoute requiredRole="admin">
      <PortalShell title="Observability" nav={ADMIN_NAV}>
        <Observability />
      </PortalShell>
    </ProtectedRoute>
  ),
});

const PLAUSIBLE_DOMAIN =
  (import.meta.env.VITE_PLAUSIBLE_DOMAIN as string | undefined) ?? "currencythoughts.net";
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

function Observability() {
  const checkSitemapFn = useServerFn(checkSitemap);
  const observabilityEnvFn = useServerFn(observabilityEnv);
  const [sitemap, setSitemap] = useState<{
    ok: boolean;
    status: number;
    lastModified: string | null;
  } | null>(null);
  const [env, setEnv] = useState<{ sentryAuthToken: boolean; sentryApiToken: boolean } | null>(
    null,
  );

  useEffect(() => {
    checkSitemapFn().then(setSitemap).catch(() => setSitemap({ ok: false, status: 0, lastModified: null }));
    observabilityEnvFn().then(setEnv).catch(() => {});
  }, [checkSitemapFn, observabilityEnvFn]);

  const missingEnv: string[] = [];
  if (!SENTRY_DSN) missingEnv.push("VITE_SENTRY_DSN");
  if (!import.meta.env.VITE_PLAUSIBLE_DOMAIN) missingEnv.push("VITE_PLAUSIBLE_DOMAIN");
  if (env && !env.sentryAuthToken) missingEnv.push("SENTRY_AUTH_TOKEN (build secret)");
  if (env && !env.sentryApiToken) missingEnv.push("SENTRY_API_TOKEN (optional)");

  return (
    <div className="space-y-8">
      <header>
        <Eyebrow>System Health</Eyebrow>
        <h1 className="mt-2 font-display text-3xl text-navy-900">Observability</h1>
        <p className="mt-2 text-sm text-charcoal-500">
          Analytics, error monitoring, and indexing infrastructure for the platform.
        </p>
      </header>

      {missingEnv.length > 0 && (
        <div className="rounded-md border border-warning/40 bg-warning/5 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 text-warning" />
            <div>
              <p className="font-medium text-charcoal-900">One-time setup needed</p>
              <p className="mt-1 text-sm text-charcoal-500">
                The following environment variables are missing. Add them to enable full
                observability:
              </p>
              <ul className="mt-2 list-disc pl-5 text-sm text-charcoal-900">
                {missingEnv.map((k) => (
                  <li key={k} className="font-mono text-xs">{k}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <StatusCard
          title="Plausible"
          ok={Boolean(PLAUSIBLE_DOMAIN)}
          badge={PLAUSIBLE_DOMAIN ? "configured" : "missing domain"}
          detail={PLAUSIBLE_DOMAIN ? `Domain: ${PLAUSIBLE_DOMAIN}` : "Set VITE_PLAUSIBLE_DOMAIN"}
          href={`https://plausible.io/${PLAUSIBLE_DOMAIN}`}
          hrefLabel="Open Plausible dashboard"
        />
        <StatusCard
          title="Sentry"
          ok={Boolean(SENTRY_DSN)}
          badge={SENTRY_DSN ? "DSN configured" : "DSN missing"}
          detail={
            SENTRY_DSN
              ? "Errors and replays are being captured."
              : "Add VITE_SENTRY_DSN to start capturing errors."
          }
          href="https://sentry.io/organizations/"
          hrefLabel="Open Sentry"
        />
        <StatusCard
          title="Sitemap"
          ok={Boolean(sitemap?.ok)}
          badge={sitemap ? (sitemap.ok ? `HTTP ${sitemap.status}` : "unreachable") : "checking…"}
          detail={
            sitemap?.lastModified
              ? `Last modified: ${sitemap.lastModified}`
              : "Served dynamically from /sitemap.xml"
          }
          href="/sitemap.xml"
          hrefLabel="View sitemap.xml"
        />
      </div>

      <section className="rounded-md border border-bronze-600/20 bg-cream-50 p-6">
        <Eyebrow>Team Bookmarks</Eyebrow>
        <h2 className="mt-2 font-display text-xl text-navy-900">Dashboards</h2>
        <p className="mt-2 text-sm text-charcoal-500">
          Quick links to the external tools that power monitoring, deliverability, and analytics.
        </p>
        <ul className="mt-4 space-y-2 text-sm">
          <DocLink href="https://plausible.io" label="Plausible — traffic & custom events" />
          <DocLink href="https://sentry.io" label="Sentry — errors, performance, replays" />
          <DocLink href="https://resend.com/emails" label="Resend — transactional email delivery" />
        </ul>
      </section>
    </div>
  );
}

function StatusCard({
  title,
  ok,
  badge,
  detail,
  href,
  hrefLabel,
}: {
  title: string;
  ok: boolean;
  badge: string;
  detail: string;
  href: string;
  hrefLabel: string;
}) {
  return (
    <div className="rounded-md border border-bronze-600/20 bg-white p-5">
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-display text-lg text-navy-900">{title}</h3>
        {ok ? (
          <CheckCircle2 className="h-5 w-5 text-success" />
        ) : (
          <AlertCircle className="h-5 w-5 text-warning" />
        )}
      </div>
      <span
        className={`mt-3 inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
          ok ? "bg-success/10 text-success" : "bg-warning/10 text-warning"
        }`}
      >
        {badge}
      </span>
      <p className="mt-3 text-sm text-charcoal-500">{detail}</p>
      <a
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-navy-800 hover:text-bronze-600"
      >
        {hrefLabel}
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
    </div>
  );
}

function DocLink({ href, label }: { href: string; label: string }) {
  return (
    <li>
      <a
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        className="inline-flex items-center gap-1.5 text-navy-800 hover:text-bronze-600"
      >
        {label}
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
    </li>
  );
}